import { FileText, Download, Trash2, Eye, MoreVertical } from 'lucide-react';
import { Document } from '@/types/document';
import { formatFileSize } from '@/services/documentApi';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface DocumentCardProps {
  document: Document;
  onView?: (doc: Document) => void;
  onDownload?: (doc: Document) => void;
  onDelete?: (doc: Document) => void;
  showUser?: boolean;
  className?: string;
}

export function DocumentCard({
  document,
  onView,
  onDownload,
  onDelete,
  showUser = false,
  className,
}: DocumentCardProps) {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div
      className={cn(
        'group relative flex flex-row items-center justify-between gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors min-w-0',
        className
      )}
    >
      <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
        <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-lg bg-primary/10 shrink-0">
          <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
        </div>
        <div className="min-w-0 flex-1 space-y-0.5 sm:space-y-1">
          <p className="font-medium truncate text-sm sm:text-base">{document.originalName}</p>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0 text-xs sm:text-sm text-muted-foreground">
            <span>{formatFileSize(document.size)}</span>
            <span className="hidden sm:inline">•</span>
            <span className="hidden sm:inline">{formatDate(document.uploadedAt)}</span>
            {showUser && document.userName && (
              <>
                <span className="hidden md:inline">•</span>
                <span className="hidden md:inline">{document.userName}</span>
              </>
            )}
            <span className="sm:hidden">•</span>
            <span className="sm:hidden">{formatDate(document.uploadedAt)}</span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1 sm:gap-2 shrink-0">
        {/* Desktop actions */}
        <div className="hidden sm:flex items-center gap-1">
          {onView && (
            <Button variant="ghost" size="icon" onClick={() => onView(document)}>
              <Eye className="h-4 w-4" />
              <span className="sr-only">View</span>
            </Button>
          )}
          {onDownload && (
            <Button variant="ghost" size="icon" onClick={() => onDownload(document)}>
              <Download className="h-4 w-4" />
              <span className="sr-only">Download</span>
            </Button>
          )}
          {onDelete && (
            <Button
              variant="ghost"
              size="icon"
              className="text-destructive hover:text-destructive"
              onClick={() => onDelete(document)}
            >
              <Trash2 className="h-4 w-4" />
              <span className="sr-only">Delete</span>
            </Button>
          )}
        </div>

        {/* Mobile actions dropdown */}
        <div className="sm:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-4 w-4" />
                <span className="sr-only">Actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onView && (
                <DropdownMenuItem onClick={() => onView(document)}>
                  <Eye className="mr-2 h-4 w-4" />
                  View
                </DropdownMenuItem>
              )}
              {onDownload && (
                <DropdownMenuItem onClick={() => onDownload(document)}>
                  <Download className="mr-2 h-4 w-4" />
                  Download
                </DropdownMenuItem>
              )}
              {onDelete && (
                <DropdownMenuItem
                  onClick={() => onDelete(document)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
